// 秘书: talking to an agent's secretary (a voice model in front of the agent; app/src/secretary/, office/secretary.ts).
// On an agent's card whose secretary is on (peer.sec): 「和 TA 说话」 opens a conversation (/rt/<room>?agent=<id>) and a
// small bar at the bottom: 「按住说话」 (hold: my mic goes to the secretary, and to the people near me as always),
// what it's doing (在听 / 想一想 / 在说), the last lines, 「结束」.
//
//   my existing microphone track ─ secretary-tap.js (16 kHz PCM16) ─ authorized samples, {t:"ptt", on} ─ /rt ─ the model
// The secretary has its own tap before Deck's delayed denoise/gate; normal voice and STT keep their own pipeline.
//   its answer: as the agent's voice track on the SFU ({t:"rt_sfu", on: true}: everyone near hears it, me as they do),
//   or until that's up (or if it can't be), binary PCM16 24 kHz to me only, queued into my audio graph at the agent's
//   loudness (扬声器 volume, nothing when voice is off); {t:"rt_flush"}: dropped at once
//
// Interrupting: pressing 按住说话 while it talks stops its voice here at once (the queue is dropped, and the agent's
// SFU track ducked to 10%, before the server even hears of it) and the server cancels the answer and stops sending. Markers: {t:"sec"} (and welcome.secs) says who talks to whose
// secretary: their name tags show 对话中 / 在听, for everyone (app.js label()).
import { t, tr, onLang, has, errorText } from '/i18n.js';
import { createSecretaryCapture } from './secretary-capture.js';
const RATE_OUT = 24000, MAX_LINES = 3;

const CSS = `
#secbar{position:fixed;left:50%;bottom:76px;transform:translateX(-50%);z-index:30;display:none;align-items:center;gap:10px;max-width:min(560px,calc(100vw - 16px));
  background:#fff8e7;border:3px solid #1b1410;border-radius:14px;box-shadow:0 3px 0 #1b1410;padding:8px 10px;font-size:13px}
#secbar.on{display:flex}
#secbar .who{font-weight:700;white-space:nowrap}
#secbar .st{font-size:12px;color:#666;white-space:nowrap}
#secbar .lines{flex:1;min-width:0;font-size:12px;color:#333;display:flex;flex-direction:column;gap:1px;max-height:48px;overflow:hidden}
#secbar .lines div{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#secbar button{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:6px 12px;background:#fff;cursor:pointer;white-space:nowrap;touch-action:none;user-select:none;-webkit-user-select:none}
#secbar .talk{background:#FDD03B;font-weight:700;padding:8px 16px}
#secbar .talk.live{background:#f59e0b}
@media (max-width:560px){#secbar{bottom:88px;flex-wrap:wrap}#secbar .lines{flex-basis:100%;order:3}}`;

/**
 * @param {object} o
 * @param {string} o.room            the office (floor) this page is in
 * @param {() => object|null} o.me
 * @param {() => Map} o.peers
 * @param {() => object|null} o.pipeline   micPipeline() / rawMic(), or null while voice is off
 * @param {() => MediaStreamTrack|null} o.microphone  the already granted getUserMedia track
 * @param {() => AudioContext|null} o.actx  the page's audio graph (voice clips play there too)
 * @param {(id) => number} o.gain   how loud that otter is to me now (loudness × 扬声器), 0 when voice is off
 * @param {(on) => void} o.hold     hold / release 按住说话 for the mic (so it's on while I talk to the secretary)
 * @param {() => boolean} o.muted
 * @param {(text) => void} o.toast
 * @param {() => void} o.changed    a marker changed (name tags, the online list)
 */
export function initSecretary(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  const bar = document.createElement('div'); bar.id = 'secbar';
  bar.innerHTML = '<span class="who"></span><span class="st"></span><div class="lines"></div><button class="talk"></button><button class="end"></button>';
  document.body.appendChild(bar);
  const q = (s) => bar.querySelector(s), talkBtn = q('.talk');
  talkBtn.textContent = t('sec.talk'); tr(q('.end'), 'sec.end');

  let enabled = false;               // this deployment has secretaries (welcome.secs)
  const pairs = new Map();           // agent id -> {with, name}: who talks to whose secretary
  let cur = null;                    // {ws, agent: peer id, name, ready, state, ptt}
  let tap = null, tapFor = null, tapTrack = null, tapGeneration = 0, tapError = false, tapRetryAt = 0;
  let talkPointer = null;
  let playing = [], nextAt = 0, gainNode = null;

  // ---- the secretary's mic tap: a separate source from the existing raw track, never a second getUserMedia
  function untap() {
    tapGeneration++;
    const x = tap; tap = null; tapFor = null; tapTrack = null;
    if (!x) return;
    x.capture.dispose();
    try { x.node.disconnect(x.worklet); x.worklet.disconnect(); x.sink.disconnect(); } catch {}
    x.own?.close().catch(() => {});
  }
  async function buildTap(p, track) {
    untap(); const generation = tapGeneration, c = cur; tapFor = p; tapTrack = track; tapError = false;
    let own = null, node = null, worklet = null, sink = null;
    draw();
    try {
      const ctx = p.ctx || (own = new AudioContext());
      node = ctx.createMediaStreamSource(new MediaStream([track]));
      await ctx.audioWorklet.addModule(new URL('./secretary-tap.js', import.meta.url).pathname);
      if (generation !== tapGeneration || cur !== c || o.pipeline() !== p || o.microphone() !== track) {
        node.disconnect(); own?.close().catch(() => {}); return;
      }
      worklet = new AudioWorkletNode(ctx, 'deck-secretary-tap', { channelCount: 1, channelCountMode: 'explicit', numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
      sink = ctx.createGain(); sink.gain.value = 0; sink.connect(ctx.destination);
      node.connect(worklet); worklet.connect(sink);
      const x = { worklet, node, sink, own, ctx, p, track, capture: null };
      tap = x;
      x.capture = createSecretaryCapture({ ctx, port: worklet.port,
        valid: () => cur === c && tap === x && o.pipeline() === p && o.microphone() === track && track.readyState === 'live' && c.ws.readyState === 1,
        send: (value) => c.ws.send(value),
        failed: () => { if (cur === c) { o.toast(t('sec.captureInterrupted')); end(); } },
      });
      worklet.onprocessorerror = () => { if (cur === c && tap === x) { o.toast(t('sec.captureInterrupted')); end(); } };
      draw();
    } catch (e) {
      try { node?.disconnect(); worklet?.disconnect(); sink?.disconnect(); } catch {}
      own?.close().catch(() => {});
      if (generation === tapGeneration && cur === c) { tap = null; tapError = true; tapRetryAt = Date.now() + 5000; draw(); }
      console.warn('secretary: no mic tap', e);
    }
  }

  function repipe() {
    if (!cur) return;
    const p = o.pipeline(), track = o.microphone();
    if (!p || !track || track.readyState !== 'live') { o.toast(t('sec.voiceOff')); end(); return; }
    if (tapFor === p && tapTrack === track) {
      if (!tap && tapError && Date.now() >= tapRetryAt) void buildTap(p, track);
      return;
    }
    // A previous graph can still have messages in flight. Do not move its active turn onto a different mic.
    if (cur.ptt || tap?.capture.running()) { o.toast(t('sec.captureChanged')); end(); return; }
    void buildTap(p, track);
  }

  // ---- its voice: PCM16 24 kHz pieces, one after another, at the agent's loudness
  function play(buf) {
    const ctx = o.actx(); if (!ctx || ctx.state === 'closed' || !cur) return;
    const pcm = new Int16Array(buf), f = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) f[i] = pcm[i] / 32768;
    const ab = ctx.createBuffer(1, f.length, RATE_OUT); ab.copyToChannel(f, 0);
    if (!gainNode || gainNode.context !== ctx) { gainNode = ctx.createGain(); gainNode.connect(ctx.destination); }
    gainNode.gain.value = o.gain(agentId());
    const src = ctx.createBufferSource(); src.buffer = ab; src.connect(gainNode);
    const at = Math.max(ctx.currentTime + 0.03, nextAt);
    src.start(at); nextAt = at + ab.duration;
    playing.push(src); src.onended = () => { playing = playing.filter((x) => x !== src); };
  }
  /** Stop its voice now: what's queued is dropped (interrupting, or the server cancelled the answer). */
  function flush() { for (const s of playing) try { s.stop(); } catch {} playing = []; nextAt = 0; }
  setInterval(() => { if (cur && gainNode) gainNode.gain.setTargetAtTime(o.gain(agentId()), gainNode.context.currentTime, 0.1); }, 300);
  /** The agent's otter now: by its login, since a reconnect gives it a new connection id (review U1). */
  function agentId() { if (!cur) return null; for (const [id, p] of o.peers()) if (p.sub === cur.sub && p.kind === 'agent') return id; return cur.agent; }

  // ---- the conversation
  // what the bar says it's doing, and why a conversation ended (rt_end's reason): catalog keys
  const STATES = { idle: 'sec.st.idle', listening: 'sec.st.listening', thinking: 'sec.st.thinking', speaking: 'sec.st.speaking', held: 'sec.st.held' };
  const ENDS = { bye: 'sec.ended.bye', idle: 'sec.ended.idle', too_long: 'sec.ended.tooLong', office: 'sec.ended.office', quota: 'sec.ended.quota', model: 'sec.ended.model', replaced: 'sec.ended.replaced' };
  function draw() {
    bar.classList.toggle('on', !!cur);
    if (!cur) return;
    q('.who').textContent = t('sec.who', { name: cur.name });
    q('.st').textContent = !cur.ready ? t('sec.connecting') : cur.state === 'held' ? t(STATES.held) : !tap ? t(tapError ? 'sec.captureUnavailable' : 'sec.capturePreparing') : STATES[cur.state] ? t(STATES[cur.state]) : '';
    talkBtn.classList.toggle('live', !!cur.ptt);
    talkBtn.disabled = !cur.ready || cur.state === 'held' || !tap;
    talkBtn.textContent = cur.ptt ? t('sec.release') : t('sec.talk');
  }
  onLang(draw);
  function line(who, text) {
    if (!cur) return;
    const box = q('.lines'), d = document.createElement('div'); d.textContent = t('sec.line', { who, text });
    box.appendChild(d); while (box.children.length > MAX_LINES) box.firstChild.remove();
  }

  /** Why the office says no (?check=1): sec.err.<code> (E_OFF without a name: the office has no secretaries yet), else
   *  the office's own words if Chinese (an older office: they're for people), else a general line. */
  const refusal = (e) => {
    const code = e?.code === 'E_OFF' && !e.params?.name ? 'E_OFF_office' : e?.code;
    return code && has(`sec.err.${code}`) ? errorText('sec.err', { ...e, code }) : /[\u4e00-\u9fff]/.test(e?.message ?? '') ? e.message : t('sec.unavailable');
  };
  let starting = false, startGeneration = 0;
  async function start(p) {
    if (starting) return; // a double click: one conversation (review U4)
    if (cur) { if (cur.sub === p.sub) return; end(); }
    if (!o.pipeline() || !o.microphone()) { o.toast(t('sec.needMic')); return; }
    const generation = startGeneration, base = `/rt/${encodeURIComponent(o.room)}?agent=${encodeURIComponent(p.id)}`;
    starting = true;
    const chk = await fetch(base + '&check=1').then((r) => r.json()).catch(() => null).finally(() => { starting = false; });
    if (generation !== startGeneration || !o.pipeline() || !o.microphone()) return; // voice may have changed while the check was pending
    if (!chk?.ok) { o.toast(refusal(chk?.error)); return; }
    if (cur) return;
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${base}`);
    ws.binaryType = 'arraybuffer';
    const c = (cur = { ws, agent: p.id, sub: p.sub, name: p.name, ready: false, state: 'idle', ptt: false });
    o.actx()?.resume?.().catch(() => {});
    ws.onmessage = (e) => {
      if (cur !== c) return;
      if (typeof e.data !== 'string') { play(e.data); return; }
      let m; try { m = JSON.parse(e.data); } catch { return; }
      if (m.t === 'rt_ready') { c.ready = true; c.name = m.name || c.name; }
      else if (m.t === 'rt_state') {
        c.state = m.state;
        // The server has already cleared the turn when hearing permission is lost. Drop all late tap messages too.
        if (m.state === 'held' && (c.ptt || tap?.capture.running())) { c.ptt = false; talkPointer = null; o.hold(false); untap(); repipe(); }
      }
      else if (m.t === 'rt_flush') flush();
      else if (m.t === 'rt_sfu') { c.sfu = !!m.on; if (c.sfu) flush(); } // its voice comes as the agent's track now, as for everyone near
      else if (m.t === 'rt_lat') console.log('secretary: first sound', m.ms, 'ms after release');
      else if (m.t === 'rt_end') { if (ENDS[m.reason]) o.toast(t(ENDS[m.reason])); }
      draw();
    };
    // (refused at the door after the check passed: today's quota, too many conversations; the socket can't say which)
    ws.onclose = () => { if (cur === c) { if (!c.ready) o.toast(t('sec.failed')); stopLocal(); } };
    repipe();
    draw();
  }
  function setPtt(on) {
    if (!cur?.ready || cur.ptt === on) return;
    if (on && o.muted()) { o.toast(t('sec.muted')); return; }
    if (on && cur.state === 'held') return;
    const c = cur, x = tap;
    if (!x || x.p !== o.pipeline() || x.track !== o.microphone()) { repipe(); return; }
    if (on) {
      flush(); o.duck?.(agentId()); // interrupting: its voice stops here at once (or ducks, on the SFU); the server cancels the rest
      x.own?.resume().catch(() => {}); // a tap of its own may still be suspended
      o.hold(true);
      if (!x.capture.start()) { o.hold(false); return; }
      c.ptt = true;
    } else {
      c.ptt = false; x.capture.release(); o.hold(false);
    }
    draw();
  }
  function stopLocal() {
    if (cur?.ptt || tap?.capture.running()) o.hold(false);
    talkPointer = null; flush(); cur = null; untap(); draw();
  }
  function end() {
    startGeneration++; // also cancel a permission check before its WebSocket exists
    const c = cur; if (!c) return;
    try { c.ws.send(JSON.stringify({ t: 'bye' })); } catch {}
    try { c.ws.close(1000, 'bye'); } catch {}
    stopLocal();
  }

  talkBtn.addEventListener('pointerdown', (e) => { if (talkPointer !== null) return; e.preventDefault(); talkPointer = e.pointerId; talkBtn.setPointerCapture?.(e.pointerId); setPtt(true); if (!cur?.ptt) talkPointer = null; });
  talkBtn.addEventListener('pointerup', (e) => { if (talkPointer !== e.pointerId) return; talkPointer = null; setPtt(false); });
  // A cancelled gesture is not a request to commit a partial turn. End the conversation so even an older server drops it.
  for (const ev of ['pointercancel', 'lostpointercapture']) talkBtn.addEventListener(ev, (e) => { if (talkPointer !== e.pointerId) return; talkPointer = null; if (cur?.ptt) { o.toast(t('sec.captureCancelled')); end(); } });
  talkBtn.addEventListener('contextmenu', (e) => e.preventDefault());
  q('.end').onclick = end;
  const cancelGesture = () => { if (cur?.ptt) { o.toast(t('sec.captureCancelled')); end(); } };
  addEventListener('blur', cancelGesture);
  document.addEventListener('visibilitychange', () => { if (document.hidden) cancelGesture(); });
  setInterval(repipe, 1000);

  return {
    /** welcome: secretaries on here (secs present), and who talks to whom right now. */
    welcome(m) {
      enabled = Array.isArray(m.secs); pairs.clear();
      for (const s of m.secs ?? []) pairs.set(s.agent, { with: s.with, name: s.name });
      o.changed(); // (a conversation goes on across a reconnect of the office socket: the Secretary knows me by my login)
    },
    /** {t:"sec"}, {t:"sec_on"} from the office. */
    msg(m) {
      if (m.t === 'sec') { if (m.with) pairs.set(m.agent, { with: m.with, name: m.name }); else pairs.delete(m.agent); }
      else if (m.t === 'sec_on') { const p = o.peers().get(m.id); if (p) p.sec = m.on || undefined; if (!m.on && p && cur?.sub === p.sub) stopLocal(); }
      o.changed();
    },
    /** A line from the office (chat or caption): kept in the bar if it's this conversation's. */
    heard(m) {
      if (!cur) return;
      if (m.t === 'chat' && m.sec && m.id === agentId()) line(t('sec.speaker.sec'), m.text);
      else if (m.t === 'caption' && !m.partial && m.id === o.me()?.id) line(t('sec.speaker.me'), m.text);
    },
    /** On an agent's card: 和 TA 说话 (or why not). */
    forCard(p) {
      if (!enabled || o.guest?.() || p.kind !== 'agent' || !p.sec) return null; // (guests can't: it's paid for; review U6)
      if (cur?.sub === p.sub) return { icon: 'mic', label: t('sec.card.active'), disabled: true, title: t('sec.card.activeTip') };
      const busy = pairs.get(p.id);
      if (busy && busy.with !== o.me()?.id) return { icon: 'mic', label: t('sec.card.busy'), disabled: true, title: t('sec.card.busyTip', { name: busy.name }) };
      return { icon: 'mic', label: t('sec.card.talk'), title: t('sec.card.talkTip'), go: () => start(p) };
    },
    /** For the name tag: 「在听」 on an agent whose secretary is in a conversation, 「对话中」 on whoever talks to one. */
    tag(p) {
      if (pairs.has(p.id)) return t('sec.tag.listening');
      for (const v of pairs.values()) if (v.with === p.id) return t('sec.tag.talking');
      return null;
    },
    talking: () => !!cur,
    repipe,
    muteChanged: (on) => { if (on && cur?.ptt) setPtt(false); },
    end,
  };
}
